// src/components/crm/CrmLoginPage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Lock, Mail, Eye, EyeOff, ArrowRight, Shield, Download, Smartphone,
  Users, BarChart3, Target, CalendarClock, MapPin, Route, Play, Apple,
} from 'lucide-react';
import { userLogin } from '../../services/auth';
import { getCurrentUser } from '../../api/auth';
import safeToast from '../../utils/safeToast';
import { applySeo } from '../../utils/seo';
import { ogBrandImageUrl } from '../../constants/ogBrand';
import type { User } from '../../types';
import CrmSignupModal from './CrmSignupModal';
import CrmLanguagePicker from './CrmLanguagePicker';
import { removeBootSplash } from '../../utils/bootSplash';

export default function CrmLoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [signupOpen, setSignupOpen] = useState(false);
  const [storeLinks, setStoreLinks] = useState<{ playStoreUrl?: string; appStoreUrl?: string }>({});

  const roleLabels = useMemo(() => ({
    client: t('crmRoleClient'),
    crm_manager: t('crmRoleManager'),
    sales_rep: t('crmRoleSalesRep'),
  }), [t]);

  const features = useMemo(() => [
    { icon: Users, title: t('crmLoginFeatureTeamTitle'), desc: t('crmLoginFeatureTeamDesc') },
    { icon: Target, title: t('crmLoginFeaturePipelineTitle'), desc: t('crmLoginFeaturePipelineDesc') },
    { icon: CalendarClock, title: t('crmLoginFeatureFollowUpTitle'), desc: t('crmLoginFeatureFollowUpDesc') },
    { icon: BarChart3, title: t('crmLoginFeatureAnalyticsTitle'), desc: t('crmLoginFeatureAnalyticsDesc') },
  ], [t]);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('crm_token') || localStorage.getItem('owner_token') || localStorage.getItem('dev_admin_token');
        if (token) {
          const user = await getCurrentUser();
          if (user && user.crmRole) {
            if (!localStorage.getItem('crm_token')) localStorage.setItem('crm_token', token);
            navigate('/crm', { replace: true });
            return;
          }
        }
      } catch { /* ignore */ }
      setChecking(false);
    })();
  }, [navigate]);

  // Deep-link from mobile app: /crm-login?signup=1 opens the signup modal.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const signup = String(params.get('signup') || params.get('register') || '').toLowerCase();
      if (['1', 'true', 'yes'].includes(signup)) {
        setSignupOpen(true);
      }
    } catch { /* ignore */ }
  }, []);

  // Keep the HTML glowing-T splash until session check finishes (no second logo).
  useEffect(() => {
    if (checking) return;
    removeBootSplash();
  }, [checking]);

  useEffect(() => () => { removeBootSplash(); }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/apk/info');
        if (res.ok) {
          const data = await res.json();
          setStoreLinks({ playStoreUrl: data?.playStoreUrl || '', appStoreUrl: data?.appStoreUrl || '' });
        }
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.tcards.org';
    const title = `${t('crmLoginBrandTitle')} — ${t('crmLoginBrandSubtitle')}`;
    const description = `${t('crmLoginHeroTitle')} ${t('crmLoginHeroDesc')}`;
    return applySeo({
      title,
      description,
      image: ogBrandImageUrl(origin, 'crm'),
      imageAlt: 'Tcards CRM',
      url: `${origin}/crm`,
      canonicalUrl: `${origin}/crm`,
      siteName: 'Tcards CRM',
    });
  }, [t]);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      safeToast('error', t('crmLoginEnterCredentials'));
      return;
    }
    setLoading(true);
    try {
      const res = await userLogin(email.trim(), password);
      if (!res.token) {
        safeToast('error', t('crmLoginInvalidCredentials'));
        setLoading(false);
        return;
      }
      localStorage.setItem('crm_token', res.token);
      localStorage.setItem('owner_token', res.token);

      const user: User | null = await getCurrentUser();
      if (!user) {
        safeToast('error', t('crmLoginVerifyFailed'));
        localStorage.removeItem('crm_token');
        localStorage.removeItem('owner_token');
        setLoading(false);
        return;
      }
      if (!user.crmRole || !['client', 'crm_manager', 'sales_rep'].includes(user.crmRole)) {
        safeToast('error', t('crmLoginNoAccess'));
        localStorage.removeItem('crm_token');
        localStorage.removeItem('owner_token');
        setLoading(false);
        return;
      }

      try {
        const access = await import('../../api/crm').then((m) => m.getCrmAccessStatus());
        if (access?.hasCrmRole && access.showCrmUi === false) {
          safeToast('error', t('crmLoginSubInactive'));
          localStorage.removeItem('crm_token');
          localStorage.removeItem('owner_token');
          setLoading(false);
          return;
        }
      } catch { /* non-fatal — allow login if access-status unreachable */ }

      safeToast('success', t('crmLoginWelcomeBackUser', { name: user.name || user.email || '' }));
      setLoading(false);
      navigate('/crm', { replace: true });
    } catch (err: any) {
      safeToast('error', err?.message || t('crmLoginFailed'));
      setLoading(false);
    }
  }

  if (checking) {
    // Match boot splash background; the HTML T splash stays on top until removeBootSplash().
    return <div className="min-h-screen bg-[#0b0b22]" aria-busy="true" />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-950 via-blue-950 to-violet-950 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl" />
        <div className="absolute top-[40%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] bg-purple-500/5 rounded-full blur-3xl" />
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
      </div>

      {/* Compact top bar — brand flush left with marketing column, language on right */}
      <header className="relative z-20 shrink-0 border-b border-white/[0.06] bg-slate-950/40 backdrop-blur-md">
        <div className="flex h-12 sm:h-14 w-full items-center justify-between gap-3 px-4 sm:px-6 lg:px-10 xl:px-16">
          <a
            href="/"
            aria-label={t('crmLoginBackToHome')}
            title={t('crmLoginBackToHome')}
            className="flex min-w-0 items-center gap-2.5 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60"
          >
            <span className="h-8 w-8 sm:h-9 sm:w-9 shrink-0 overflow-hidden rounded-lg shadow-md shadow-violet-500/20 ring-1 ring-white/10">
              <img src="/favicon.svg" alt="" className="h-full w-full object-cover" />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm sm:text-base font-bold tracking-tight text-white leading-tight">
                {t('crmLoginBrandTitle')}
              </span>
              <span className="hidden sm:block truncate text-[9px] font-medium uppercase tracking-[0.12em] text-violet-300/55 leading-tight">
                {t('crmLoginBrandSubtitle')}
              </span>
            </span>
          </a>
          <CrmLanguagePicker variant="dark" className="shrink-0" />
        </div>
      </header>

      <div className="relative z-10 flex flex-1 flex-col lg:flex-row">
        {/* Marketing column — top-aligned (was vertically centered → large empty header gap) */}
        <div className="hidden lg:flex lg:w-1/2 flex-col justify-start px-10 xl:px-16 pt-8 xl:pt-10 pb-10">
          <div className="max-w-lg">
            <p className="text-sm text-blue-200/65 mb-5 leading-relaxed">
              {t('crmLoginIntroDesc')}
            </p>

            <h2 className="text-3xl xl:text-[2.35rem] font-bold leading-[1.15] mb-3 bg-gradient-to-r from-white via-blue-100 to-violet-200 bg-clip-text text-transparent">
              {t('crmLoginHeroTitle')}
            </h2>
            <p className="text-base text-blue-200/55 mb-7 leading-relaxed">
              {t('crmLoginHeroDesc')}
            </p>

            <div className="grid sm:grid-cols-2 gap-2.5 mb-6">
              {features.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className="group rounded-xl border border-white/[0.07] bg-white/[0.03] p-3.5 hover:bg-white/[0.06] hover:border-white/[0.12] transition-all"
                >
                  <div className="mb-2.5 flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500/20 to-violet-500/20 border border-white/[0.08] group-hover:from-blue-500/30 group-hover:to-violet-500/30 transition-all">
                    <Icon className="w-4 h-4 text-blue-300" />
                  </div>
                  <h3 className="text-sm font-semibold text-white/90">{title}</h3>
                  <p className="text-xs text-blue-200/40 mt-0.5 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>

            <div className="rounded-xl border border-indigo-400/25 bg-gradient-to-br from-indigo-500/10 to-blue-500/5 p-4">
              <div className="flex items-start gap-3 mb-2.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-500/20 border border-indigo-400/30">
                  <Route className="w-4 h-4 text-indigo-300" />
                </div>
                <h3 className="text-sm font-semibold text-white flex items-center gap-2 pt-1.5">
                  <MapPin className="w-3.5 h-3.5 text-indigo-300" />
                  {t('crmLoginRouteSectionTitle')}
                </h3>
              </div>
              <p className="text-sm text-blue-200/50 leading-relaxed mb-2">
                {t('crmLoginRouteSectionDesc')}
              </p>
              <p className="text-xs text-indigo-200/40 italic mb-3">
                {t('crmLoginRouteComingSoon')}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <a
                  href="/download"
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-indigo-400/30 bg-indigo-500/10 hover:bg-indigo-500/20 text-sm font-semibold text-indigo-100 transition-all"
                >
                  <Smartphone className="w-4 h-4" />
                  {t('crmLoginDownloadApp')}
                </a>
                {storeLinks.playStoreUrl && (
                  <a
                    href={storeLinks.playStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-green-400/30 bg-green-500/10 hover:bg-green-500/20 text-sm font-semibold text-green-100 transition-all"
                  >
                    <Play className="w-4 h-4" />
                    {t('crmLoginGetOnGooglePlay')}
                  </a>
                )}
                {storeLinks.appStoreUrl && (
                  <a
                    href={storeLinks.appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-white/20 bg-white/10 hover:bg-white/20 text-sm font-semibold text-white transition-all"
                  >
                    <Apple className="w-4 h-4" />
                    {t('crmLoginGetOnAppStore')}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Login column */}
        <div className="flex-1 flex items-start lg:items-center justify-center px-4 sm:px-8 lg:px-12 py-6 sm:py-8 lg:py-8">
          <div className="w-full max-w-md">
            <div className="relative">
              <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-blue-500/20 via-indigo-500/20 to-purple-500/20 blur-xl opacity-50" />

              <div className="relative bg-white/[0.06] backdrop-blur-2xl border border-white/[0.1] rounded-2xl p-6 sm:p-8 shadow-2xl shadow-black/20">
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Shield className="w-3.5 h-3.5 text-blue-400" />
                    <span className="text-[11px] font-medium text-blue-400/80 uppercase tracking-wider">{t('crmLoginSecureLogin')}</span>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white">{t('crmLoginWelcomeBack')}</h2>
                  <p className="text-sm text-white/40 mt-1">{t('crmLoginSignInDesc')}</p>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-6">
                  {(['client', 'crm_manager', 'sales_rep'] as const).map((role) => (
                    <div
                      key={role}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.06] border border-white/[0.08] text-[11px] font-medium text-blue-300/70"
                    >
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        role === 'client' ? 'bg-emerald-400' :
                        role === 'crm_manager' ? 'bg-amber-400' : 'bg-blue-400'
                      }`} />
                      {roleLabels[role]}
                    </div>
                  ))}
                </div>

                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5">{t('crmLoginEmailLabel')}</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none">
                        <Mail className="w-4 h-4 text-white/30" />
                      </div>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('crmLoginEmailPlaceholder')}
                        className="w-full bg-white/[0.04] border border-white/[0.1] rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all"
                        autoComplete="email"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5">{t('crmLoginPasswordLabel')}</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none">
                        <Lock className="w-4 h-4 text-white/30" />
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t('crmLoginPasswordPlaceholder')}
                        className="w-full bg-white/[0.04] border border-white/[0.1] rounded-xl pl-10 pr-12 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all"
                        autoComplete="current-password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-white/30 hover:text-white/60 transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full relative group flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 disabled:from-blue-800 disabled:to-violet-800 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl shadow-lg shadow-violet-600/20 hover:shadow-violet-500/30 transition-all duration-200 text-sm"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t('crmLoginSigningIn')}
                      </>
                    ) : (
                      <>
                        {t('crmLoginSubmit')}
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-6 pt-5 border-t border-white/[0.06] space-y-2.5">
                  <a
                    href="/download"
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-white/[0.12] bg-white/[0.04] hover:bg-white/[0.08] hover:border-white/[0.18] text-sm font-semibold text-blue-200/90 hover:text-white transition-all"
                  >
                    <Smartphone className="w-4 h-4" />
                    {t('crmLoginDownloadApp')}
                    <Download className="w-4 h-4 opacity-60" />
                  </a>
                  {(storeLinks.playStoreUrl || storeLinks.appStoreUrl) && (
                    <div className="flex flex-wrap items-center justify-center gap-2">
                      {storeLinks.playStoreUrl && (
                        <a
                          href={storeLinks.playStoreUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 min-w-[9rem] flex items-center justify-center gap-2 py-2 rounded-xl border border-green-400/20 bg-green-500/10 hover:bg-green-500/20 text-xs font-semibold text-green-100 transition-all"
                        >
                          <Play className="w-4 h-4" />
                          {t('crmLoginGetOnGooglePlay')}
                        </a>
                      )}
                      {storeLinks.appStoreUrl && (
                        <a
                          href={storeLinks.appStoreUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 min-w-[9rem] flex items-center justify-center gap-2 py-2 rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 text-xs font-semibold text-white transition-all"
                        >
                          <Apple className="w-4 h-4" />
                          {t('crmLoginGetOnAppStore')}
                        </a>
                      )}
                    </div>
                  )}
                  <p className="text-center text-[11px] text-white/30">
                    {t('crmLoginDownloadAppHint')}
                  </p>
                  <p className="text-center text-xs text-white/30">
                    {t('crmLoginNewUser')}{' '}
                    <button
                      type="button"
                      onClick={() => setSignupOpen(true)}
                      className="text-blue-400/70 hover:text-blue-400 transition-colors font-medium"
                    >
                      {t('crmLoginSignUp')}
                    </button>
                  </p>
                  <p className="text-center text-[11px] text-white/20">
                    {t('crmLoginPlatinumIncluded')}
                  </p>
                </div>
              </div>
            </div>

            <CrmSignupModal
              open={signupOpen}
              onClose={() => setSignupOpen(false)}
              onComplete={() => {
                setSignupOpen(false);
                navigate('/crm', { replace: true });
              }}
            />

            <p className="text-center text-[11px] text-white/20 mt-5">
              {t('crmLoginTermsFooter')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
